import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Output,
  ViewChild,
} from '@angular/core';
import { ButtonComponent, InputComponent } from '@app-ui';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgIf } from '@angular/common';
import { ApiService, BaseComponent, CustomToasterService } from '@app-utils';
import { finalize, interval, Subscription, takeUntil } from 'rxjs';
import { AuthFlowService } from '../../services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Register verification component
 */
@Component({
  selector: 'auth-register-verification',
  templateUrl: './register-verification.component.html',
  styleUrl: './register-verification.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    InputComponent,
    ButtonComponent,
    RouterLink,
    FormsModule,
    TranslateModule,
  ],
})
export class RegisterVerificationComponent
  extends BaseComponent
  implements AfterViewInit
{
  private readonly _apiSvc = inject(ApiService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _authFlowSvc = inject(AuthFlowService);
  private readonly _toast = inject(CustomToasterService);
  private readonly _translate = inject(TranslateService);

  private static readonly _EMAIL_FONT_MIN_PX = 11;

  @ViewChild('emailField') private _emailInput?: InputComponent;

  step: 'init' | 'verify' = 'init';

  email = '';

  otp = '';

  seconds = 60;

  @Output() verified = new EventEmitter<string>();

  subs: Subscription = new Subscription();

  ngAfterViewInit(): void {
    queueMicrotask(() => this._fitEmailFontSize());
  }

  onEmailModelChange(): void {
    queueMicrotask(() => this._fitEmailFontSize());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this._fitEmailFontSize();
  }

  private _fitEmailFontSize(): void {
    if (this.step !== 'init') return;
    const el = this._emailInput?.input?.nativeElement as
      | HTMLInputElement
      | undefined;
    if (!el) return;

    el.style.fontSize = '';
    if (!this.email) {
      this._cdr.markForCheck();
      return;
    }

    void el.offsetWidth;
    let fontPx = parseFloat(getComputedStyle(el).fontSize);
    const minPx = RegisterVerificationComponent._EMAIL_FONT_MIN_PX;

    el.style.fontSize = `${fontPx}px`;
    while (el.scrollWidth > el.clientWidth && fontPx > minPx) {
      fontPx -= 0.5;
      el.style.fontSize = `${fontPx}px`;
    }

    this._cdr.markForCheck();
  }

  sendOtp(flag = false): void {
    if (this.isDisableInit || this.loading$.value) return;
    this.otp = '';
    this.loading$.next(true);
    this._apiSvc
      .sendToken(this.email.trim())
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe((res) => {
        console.log('=================', res);
        this._authFlowSvc.setTempToken$(res.data.accessToken);
        this.step = 'verify';
        this.seconds = 59;
        this._startTimer();
        if (!flag) return;
        this._toast.success(
          this._translate.instant(
            'auth.component.register-verification.message.send-success',
          ),
        );
      });
  }

  private _startTimer(): void {
    this.subs.unsubscribe();
    this.subs = interval(1000)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.seconds--;
        if (this.seconds <= 0) {
          this.seconds = 0;
          this.subs.unsubscribe();
        }
        this._cdr.detectChanges();
      });
  }

  verifyOtp(): void {
    if (this.isDisableVerify || this.loading$.value) return;

    const token = this._authFlowSvc.getTempToken();
    if (!token) throw 'Token is not provided yet';

    this.loading$.next(true);
    console.log('============verify', this.otp);

    this._apiSvc
      .verifyToken(this.email, this.otp, token)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe((res) => {
        this.verified.emit(this.email);
      });
  }

  get isDisableInit(): boolean {
    return !this.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
  }

  get isDisableVerify(): boolean {
    return this.otp.length < 5;
  }
}
